import React from "react";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { formatInstantDateShortTr } from "../../../utils/appTime";
import {
  getOrderStatusDisplay,
  inferOrderDiscountScope,
  clampPctView,
  orderLineDisplayAmount,
  formatOrderLineDiscount,
  orderHasDiscountBlock,
  getRejectReasonFromLogs,
  formatPaymentMethod,
  computeOrderMoneyTotals,
} from "./orderDetailUtils";
import ProductPrice from "../../../components/CustomUI/ProductPrice";
import { useExchangeRateOptional } from "../../../contexts/exchangeRate";
import "./orderDetail.css";

const val = (v) => (v === 0 || v ? v : "-");

const bankLines = (order) => {
  if (Array.isArray(order.bankAccounts) && order.bankAccounts.length) {
    return order.bankAccounts;
  }
  if (order.bankRecipientName || order.bankIban || order.bankBranchName) {
    return [
      {
        label: "Hesap",
        bankRecipientName: order.bankRecipientName,
        bankBranchName: order.bankBranchName,
        bankIban: order.bankIban,
      },
    ];
  }
  return [];
};

const DetailGrid = ({ children }) => (
  <div className="order-detail-grid mt-2">{children}</div>
);

const DetailRow = ({ label, children, full = false }) => (
  <div className={`detail-row${full ? " detail-row-full" : ""}`}>
    <span className="detail-label">{label}</span>
    <span>{children}</span>
  </div>
);

const OrderDetailBody = ({
  order,
  format,
  rejectReasonOverride,
  onNavigateToSale,
  detailSection = "all",
}) => {
  const fx = useExchangeRateOptional();
  if (!order) {
    return <p className="text-muted mb-0">Teklif detayı yüklenemedi.</p>;
  }

  const showInfo = detailSection === "all" || detailSection === "info";
  const showModels = detailSection === "all" || detailSection === "models";
  const showDescriptions =
    detailSection === "all" || detailSection === "descriptions";

  const rejectReason =
    rejectReasonOverride || getRejectReasonFromLogs(order) || "";
  const dsc = inferOrderDiscountScope(order);
  const saleId =
    typeof order.saleId === "string" ? order.saleId : order.saleId?._id;
  const statusDisplay = getOrderStatusDisplay(order, order.workflowSteps);
  const accounts = bankLines(order);
  const terms =
    order.pdfDescription ||
    order.pdfContent?.salesTerms ||
    order.pdfContent?.description ||
    "";
  const moneyTotals = computeOrderMoneyTotals(order);
  const notes = order.notes;
  const ekBilgi = order.ekBilgiNotu;
  const company = order.companyId || {};

  const hasPaymentDelivery =
    order.paymentMethod ||
    ekBilgi ||
    order.teslimatYontemi ||
    order.opsiyonSuresi;
  const hasDescriptions = !!(
    hasPaymentDelivery ||
    terms ||
    accounts.length ||
    notes
  );

  return (
    <div className="order-detail">
      {/* 1. PDF: müşteri / teklif üst bilgileri */}
      {showInfo ? (
        <>
          <div className="detail-section mb-0">
            <strong>Teklif Bilgileri</strong>
            <DetailGrid>
              <DetailRow label="Müşteri Ünvanı">
                {company.name || "-"}
                {company.code ? ` (${company.code})` : ""}
              </DetailRow>
              <DetailRow label="Yetkili Kişi">
                {val(order.yetkiliKisi || company.yetkiliKisi)}
              </DetailRow>
              <DetailRow label="Tebligat Adresi">
                {val(company.address)}
              </DetailRow>
              <DetailRow label="Vergi Dairesi / No">
                {[company.taxOffice, company.taxNumber]
                  .filter(Boolean)
                  .join(" / ") || "-"}
              </DetailRow>
              <DetailRow label="E-posta">{val(company.email)}</DetailRow>
              <DetailRow label="Sevk Adresi">{val(company.address)}</DetailRow>
              <DetailRow label="Teklif No">{val(order.orderName)}</DetailRow>
              <DetailRow label="Teklif Tarihi">
                {order.orderDate
                  ? formatInstantDateShortTr(order.orderDate)
                  : "-"}
              </DetailRow>
              <DetailRow label="Sevk / Termin Tarihi">
                {order.terminTarihi
                  ? formatInstantDateShortTr(order.terminTarihi)
                  : "-"}
              </DetailRow>
              <DetailRow label="Döviz Kuru">
                {order.exchangeRate
                  ? `1 USD = ${Number(order.exchangeRate).toLocaleString("tr-TR", {
                      minimumFractionDigits: 4,
                      maximumFractionDigits: 4,
                    })} TL`
                  : fx?.rate
                    ? `1 USD = ${Number(fx.rate).toLocaleString("tr-TR", {
                        minimumFractionDigits: 4,
                        maximumFractionDigits: 4,
                      })} TL *`
                    : "-"}
              </DetailRow>
            </DetailGrid>
          </div>

          <div className="detail-section mb-0">
            <strong>Durum &amp; İşlem Bilgileri</strong>
            <DetailGrid>
              <DetailRow label="Durum">
                <div className="order-status-cell">
                  <Lozenge appearance={statusDisplay.appearance}>
                    {statusDisplay.label}
                  </Lozenge>
                  {order.status === "accepted" &&
                    saleId &&
                    onNavigateToSale && (
                      <button
                        type="button"
                        className="order-sale-nav-link"
                        onClick={() => onNavigateToSale(saleId)}
                      >
                        Siparişa git →
                      </button>
                    )}
                </div>
              </DetailRow>
              <DetailRow label="Teklifi Hazırlayan">
                {val(
                  order.teklifiHazirlayan ||
                    order.createdBy?.name ||
                    order.createdBy,
                )}
              </DetailRow>
              <DetailRow label="Toplam Ürün">
                {val(order.totalDevices)}
              </DetailRow>
              {order.userApprovedBy?.name && (
                <DetailRow label="Müşteri Onayı">
                  {order.userApprovedBy.name}
                  {order.userApprovedAt
                    ? ` · ${formatInstantDateShortTr(order.userApprovedAt)}`
                    : ""}
                </DetailRow>
              )}
              {(order.status === "rejected" || rejectReasonOverride) && (
                <DetailRow label="Red Nedeni">
                  <span className="text-muted">{rejectReason || "-"}</span>
                </DetailRow>
              )}
              {orderHasDiscountBlock(order) && (
                <>
                  <DetailRow label="İskonto Kapsamı">
                    {dsc === "order_total"
                      ? "Genel toplam (liste üzerinden)"
                      : "Model bazlı (%)"}
                  </DetailRow>
                  {dsc === "order_total" && (
                    <DetailRow label="Genel İskonto">
                      {Number(order.orderDiscountPercent) > 0
                        ? `%${clampPctView(order.orderDiscountPercent)}`
                        : Number(order.orderDiscountFixed) > 0
                          ? format(order.orderDiscountFixed, "money")
                          : "—"}
                    </DetailRow>
                  )}
                </>
              )}
            </DetailGrid>
          </div>
        </>
      ) : null}

      {/* 2. PDF: ürün tablosu + toplamlar */}
      {showModels ? (
        <>
          <div className="detail-section mb-0">
            <strong>Modeller</strong>
            <div className="mt-2">
              <Table
                columns={[
                  { id: 0, name: "Kod", dataType: "text" },
                  { id: 1, name: "Model", dataType: "text" },
                  { id: 2, name: "Adet", dataType: "number" },
                  { id: 3, name: "Birim Fiyat", dataType: "number" },
                  {
                    id: 4,
                    name: dsc === "order_total" ? "İskonto" : "İskonto %",
                    dataType: "number",
                  },
                  { id: 5, name: "Satır tutarı", dataType: "number" },
                ]}
              >
                {(order.models || []).map((m, i) => (
                  <Tr key={i} id={i} data={m}>
                    <Td>
                      <span className="fw-semibold">
                        {m.modelId?.code || "-"}
                      </span>
                    </Td>
                    <Td>{m.modelId?.name || "-"}</Td>
                    <Td>{m.count || 0}</Td>
                    <Td>
                      <span className="text-muted">
                        <ProductPrice
                          value={m.modelId?.price || 0}
                          rate={order.exchangeRate}
                        />
                      </span>
                    </Td>
                    <Td>
                      <span className="text-muted">
                        {formatOrderLineDiscount(m, order, format)}
                      </span>
                    </Td>
                    <Td>
                      <span className="text-primary fw-semibold">
                        <ProductPrice
                          value={orderLineDisplayAmount(m, order)}
                          rate={order.exchangeRate}
                        />
                      </span>
                    </Td>
                  </Tr>
                ))}
              </Table>
            </div>
          </div>

          <div className="detail-totals">
            {moneyTotals.hasDiscount ? (
              <>
                <div className="detail-totals-row">
                  <span>Liste ara toplamı (iskonto öncesi)</span>
                  <span>
                    <ProductPrice
                      value={moneyTotals.listSubtotal}
                      rate={order.exchangeRate}
                    />
                  </span>
                </div>
                <div className="detail-totals-row">
                  <span>
                    İskonto
                    {moneyTotals.discountScope === "order_total" &&
                    Number(order.orderDiscountPercent) > 0
                      ? ` (%${clampPctView(order.orderDiscountPercent)})`
                      : moneyTotals.discountScope === "per_line"
                        ? " (model bazlı)"
                        : ""}
                  </span>
                  <span className="text-danger">
                    −
                    <ProductPrice
                      value={moneyTotals.discountAmount}
                      rate={order.exchangeRate}
                    />
                  </span>
                </div>
              </>
            ) : null}
            <div className="detail-totals-row">
              <span>Ara toplam (iskonto sonrası)</span>
              <span>
                <ProductPrice
                  value={moneyTotals.netTotal}
                  rate={order.exchangeRate}
                />
              </span>
            </div>
            <div className="detail-totals-row">
              <span>KDV (%20)</span>
              <span>
                <ProductPrice
                  value={moneyTotals.kdv}
                  rate={order.exchangeRate}
                />
              </span>
            </div>
            <div className="detail-totals-row detail-totals-grand">
              <span>Genel Toplam (KDV dahil)</span>
              <span className="text-primary">
                <ProductPrice
                  value={moneyTotals.grandTotal}
                  rate={order.exchangeRate}
                />
              </span>
            </div>
          </div>
        </>
      ) : null}

      {/* 3–6. PDF: ödeme satırı → koşullar → banka → notlar */}
      {showDescriptions && hasDescriptions ? (
        <>
          {hasPaymentDelivery ? (
            <div className="detail-section mb-0">
              <strong>Ödeme &amp; Teslimat</strong>
              <DetailGrid>
                <DetailRow label="Ödeme Şekli">
                  {formatPaymentMethod(order.paymentMethod)}
                </DetailRow>
                <DetailRow label="Ek Bilgi Notu">{val(ekBilgi)}</DetailRow>
                <DetailRow label="Teslimat Yöntemi">
                  {val(order.teslimatYontemi)}
                </DetailRow>
                <DetailRow label="Opsiyon Süresi">
                  {val(order.opsiyonSuresi)}
                </DetailRow>
              </DetailGrid>
            </div>
          ) : null}

          {terms ? (
            <div className="detail-section mb-0">
              <strong>Satış Koşulları</strong>
              <DetailGrid>
                <DetailRow label="Koşullar" full>
                  <span
                    className="text-muted"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {terms}
                  </span>
                </DetailRow>
              </DetailGrid>
            </div>
          ) : null}

          {accounts.length > 0 ? (
            <div className="detail-section mb-0">
              <strong>Banka Hesapları</strong>
              <DetailGrid>
                {accounts.map((acc, i) => (
                  <React.Fragment key={i}>
                    <DetailRow
                      label={
                        accounts.length > 1
                          ? `Banka Hesabı ${i + 1}`
                          : "Banka Hesabı"
                      }
                    >
                      {acc.label || "Hesap"}
                    </DetailRow>
                    <DetailRow label="Alıcı">
                      {val(acc.bankRecipientName)}
                    </DetailRow>
                    <DetailRow label="Banka / Şube">
                      {val(acc.bankBranchName)}
                    </DetailRow>
                    <DetailRow label="IBAN">
                      <span className="text-muted small">
                        {val(acc.bankIban)}
                      </span>
                    </DetailRow>
                  </React.Fragment>
                ))}
              </DetailGrid>
            </div>
          ) : null}

          {notes ? (
            <div className="detail-section mb-0">
              <strong>Notlar</strong>
              <DetailGrid>
                <DetailRow label="Notlar" full>
                  <span
                    className="text-muted"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {notes}
                  </span>
                </DetailRow>
              </DetailGrid>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default OrderDetailBody;
